import type { HTMLAttributes, Ref } from 'react';
import { Children, isValidElement } from 'react';
import { HeadingContext } from 'react-aria-components/Heading';
import { Provider } from 'react-aria-components/slots';
import { TextContext } from 'react-aria-components/Text';
import { cx } from 'class-variance-authority';

import { ButtonContext } from './Button';
import { ButtonGroupContext } from './ButtonGroup';
import { Carousel } from './Carousel';
import { LinkButtonContext } from './LinkButton';

import styles from './styles/OnboardingEmptyState.module.css';

interface OnboardingEmptyStateProps extends HTMLAttributes<HTMLDivElement> {
	ref?: Ref<HTMLDivElement>;
}

/**
 * A first-time onboarding empty state: a 50/50 editorial layout with a heading, body copy, and calls
 * to action on one side and a media panel (a single image or an auto-advancing `Carousel`) on the
 * other.
 *
 * Reach for `EmptyState` instead when the surface is empty for an ordinary reason — no search
 * results, a cleared filter, a deleted last item — rather than because the user has never used the
 * feature. This component is for that first-run moment only.
 *
 * Like `EmptyState`, this follows the React Spectrum
 * [IllustratedMessage](https://react-spectrum.adobe.com/v3/IllustratedMessage.html) composition
 * pattern: consumers compose the slots as children rather than passing opaque content props. The
 * media (`Carousel`) is the media slot; a `Heading`, `Text`, and a `ButtonGroup` of buttons fill the
 * content slot.
 *
 * The component themes its slotted children through a React Aria `Provider`: it injects the editorial
 * typography classes onto `Heading`/`Text`/`ButtonGroup`, and defaults its buttons to `size="large"`.
 * Because these are slots, consumers keep full control of everything else — button `variant`, whether
 * an action is a `LinkButton` (navigation) or a `Button` (in-app action), and any explicit `size`
 * override.
 *
 * Children are partitioned by component identity, not by position: the first `Carousel` child is
 * routed to the media region and everything else is rendered in the content region, so the `Carousel`
 * can be placed anywhere among the children. If no `Carousel` is present the media region renders
 * empty. The two regions sit in a full-height row inside a shared scrollport so they stay 50/50 at
 * tablet and up and scroll together.
 *
 * @example
 * ```tsx
 * <OnboardingEmptyState>
 *   <Carousel media={items} />
 *   <Heading size="large">Release code safely in production</Heading>
 *   <Text>Control how and when changes reach your users.</Text>
 *   <ButtonGroup>
 *     <LinkButton variant="primary" href={createHref}>Get started</LinkButton>
 *     <LinkButton href={docsHref}>See an example</LinkButton>
 *   </ButtonGroup>
 * </OnboardingEmptyState>
 * ```
 */
const OnboardingEmptyState = ({ className, children, ref, ...props }: OnboardingEmptyStateProps) => {
	const items = Children.toArray(children).filter(isValidElement);
	const carouselIndex = items.findIndex((child) => child.type === Carousel);
	const carouselChild = carouselIndex === -1 ? null : items[carouselIndex];
	const contentChildren = items.filter((_, index) => index !== carouselIndex);

	return (
		<div ref={ref} {...props} className={cx(styles.root, className)}>
			<div className={styles.row}>
				<div className={styles.content}>
					<Provider
						values={[
							[HeadingContext, { className: styles.heading }],
							[TextContext, { className: styles.description }],
							[ButtonGroupContext, { className: styles.ctas }],
							[ButtonContext, { size: 'large' }],
							[LinkButtonContext, { size: 'large' }],
						]}
					>
						{contentChildren}
					</Provider>
				</div>
				<div className={styles.media}>{carouselChild}</div>
			</div>
		</div>
	);
};

export { OnboardingEmptyState };
export type { OnboardingEmptyStateProps };
